// A RETAINER'S TERM AT A GLANCE — one cell per month, oldest first, coloured by the state
// of that month's money. It replaces an eight-column table in which a paid month, a late
// one and a future one looked alike until each row was read.
//
// Pick a month to open its detail and act on it. The state of a cell is read from the
// server's own row — `balance`, `overdue`, `gated`, `kind` — and never re-derived.
import { useState } from 'react';
import he from '../../i18n/he';
import Money from '../../components/Money';
import DateText from '../../components/DateText';
import ChargeState from '../../components/ChargeState';
import ChargeFiles from './ChargeFiles';
import RowActions from './RowActions';
import { PartlyPaid } from './ActionItem';

const t = he.collections.board;
const lg = he.moneyScreen.legend;
const p = he.moneyScreen.item;

const SETTLED = 0.005;

export const monthState = (m) => {
  if (m.kind === 'recorded') {
    if (!(m.balance > SETTLED)) return 'paid';
    return m.overdue ? 'late' : 'owed';
  }
  if (m.kind === 'skipped') return 'skipped';
  return m.gated ? 'tobill' : 'future';
};

const ACT = { paid: 'recorded', late: 'recorded', owed: 'recorded', tobill: 'bill_month', skipped: 'skipped' };
const WORD = { paid: lg.paid, late: lg.late, owed: lg.owed, tobill: lg.toBill, skipped: lg.skipped, future: lg.future };

function Detail({ card, m, acts }) {
  const state = monthState(m);
  return (
    <div className={`mp-month-detail mp-state-${state}`}>
      <div className="mp-month-detail-main">
        <strong><DateText value={m.month} /></strong>
        {m.kind === 'recorded' ? <ChargeState row={m} /> : <span className="muted">{WORD[state]}</span>}
        {m.outOfTerm && <span className="badge gray">{t.outOfTerm}</span>}
        {m.kind === 'recorded' && (
          <span className="mp-figures">
            <span>{t.amount} <Money value={m.amount} /></span>
            <PartlyPaid row={m} />
            {m.collectible && <span>{m.overdue ? p.lateSince : p.dueBy} <DateText value={m.dueDate} /></span>}
          </span>
        )}
        {m.kind === 'expected_month' && m.expected != null && (
          <span className="muted">{t.monthly} {p.monthlyByContract}: <Money value={m.expected} /></span>
        )}
      </div>
      <div className="mp-month-detail-side">
        {m.kind === 'recorded' && m.collectible && <Money className="mp-amount" value={m.balance} />}
        {m.kind === 'recorded' && <ChargeFiles row={m} onChanged={acts.onFilesChanged} />}
        {ACT[state] && <RowActions kind={ACT[state]} card={card} row={m} month={m.month} acts={acts} />}
      </div>
    </div>
  );
}

export default function MonthStrip({ card, acts }) {
  const [picked, setPicked] = useState(null);
  const months = [...card.months].reverse();
  const current = months.find((m) => m.month === picked);
  if (months.length === 0) return <p className="muted small-note">{t.noMonths}</p>;
  return (
    <>
      <div className="mp-strip" role="list">
        {months.map((m) => {
          const state = monthState(m);
          return (
            <button key={`${m.month}-${m.kind}`} type="button" role="listitem"
              className={`mp-cell mp-state-${state}${picked === m.month ? ' picked' : ''}`}
              title={WORD[state]} aria-pressed={picked === m.month}
              onClick={() => setPicked(picked === m.month ? null : m.month)}>
              <DateText value={m.month} />
            </button>
          );
        })}
      </div>
      {current
        ? <Detail card={card} m={current} acts={acts} />
        : null}

    </>
  );
}

// The key to the colours, drawn once above the clients.
export function Legend() {
  return (
    <div className="mp-legend">
      {['paid', 'owed', 'late', 'tobill', 'skipped', 'future'].map((s) => (
        <span key={s} className={`mp-legend-item mp-state-${s}`}><span className="mp-swatch" />{WORD[s]}</span>
      ))}
    </div>
  );
}
